import { supabase } from '../lib/supabase';

export const seekerIntakeService = {
  /**
   * Create or update seeker intake form
   */
  async saveIntake(intakeData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for database
      const dbData = {
        user_id: user?.id,
        preferred_name: intakeData?.preferredName,
        age_range: intakeData?.ageRange,
        gender: intakeData?.gender || null,
        population_segment: intakeData?.populationSegment,
        languages: intakeData?.languages,
        cultural_background: intakeData?.culturalBackground,
        religion: intakeData?.religion,
        prefer_same_culture: intakeData?.preferSameCulture,
        open_to_any_culture: intakeData?.openToAnyCulture,
        current_issues: intakeData?.currentIssues,
        symptoms: intakeData?.symptoms,
        duration: intakeData?.duration,
        additional_context: intakeData?.additionalContext,
        has_existing_conditions: intakeData?.hasExistingConditions,
        currently_in_treatment: intakeData?.currentlyInTreatment,
        urgency_level: intakeData?.urgencyLevel || null,
        impact_level: intakeData?.impactLevel || null,
        self_harm_thoughts: intakeData?.selfHarmThoughts,
        harm_to_others: intakeData?.harmToOthers,
        expand_search_radius: intakeData?.expandSearchRadius,
        helper_type: intakeData?.helperType,
        preferred_location: intakeData?.preferredLocation,
        session_formats: intakeData?.sessionFormats,
        prefer_same_gender: intakeData?.preferSameGender,
        open_to_any_gender: intakeData?.openToAnyGender,
        require_expertise: intakeData?.requireExpertise,
        data_consent: intakeData?.dataConsent,
        privacy_settings: intakeData?.privacySettings,
        agree_to_terms: intakeData?.agreeToTerms,
        understand_limitations: intakeData?.understandLimitations,
        reflective_prompts_responses: intakeData?.reflectivePromptsResponses,
        voice_dictation_transcript: intakeData?.voiceDictationTranscript,
        context_media_urls: intakeData?.contextMediaUrls,
        completed_at: new Date()?.toISOString()
      };

      // Check if intake already exists
      const { data: existing } = await supabase?.from('seeker_intakes')?.select('id')?.eq('user_id', user?.id)?.single();

      let result;
      if (existing) {
        // Update existing intake
        result = await supabase?.from('seeker_intakes')?.update(dbData)?.eq('user_id', user?.id)?.select()?.single();
      } else {
        // Create new intake
        result = await supabase?.from('seeker_intakes')?.insert(dbData)?.select()?.single();
      }

      if (result?.error) throw result?.error;
      return { data: result?.data, error: null };
    } catch (error) {
      console.error('Save intake error:', error);
      return { data: null, error };
    }
  },

  /**
   * Get user's intake data
   */
  async getIntake() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('seeker_intakes')?.select('*')?.eq('user_id', user?.id)?.single();

      if (error && error?.code !== 'PGRST116') throw error; // Ignore "not found" error

      // Convert snake_case to camelCase
      if (data) {
        return {
          data: {
            preferredName: data?.preferred_name,
            ageRange: data?.age_range,
            gender: data?.gender,
            populationSegment: data?.population_segment,
            languages: data?.languages,
            culturalBackground: data?.cultural_background,
            religion: data?.religion,
            preferSameCulture: data?.prefer_same_culture,
            openToAnyCulture: data?.open_to_any_culture,
            currentIssues: data?.current_issues,
            symptoms: data?.symptoms,
            duration: data?.duration,
            additionalContext: data?.additional_context,
            hasExistingConditions: data?.has_existing_conditions,
            currentlyInTreatment: data?.currently_in_treatment,
            urgencyLevel: data?.urgency_level,
            impactLevel: data?.impact_level,
            selfHarmThoughts: data?.self_harm_thoughts,
            harmToOthers: data?.harm_to_others,
            expandSearchRadius: data?.expand_search_radius,
            helperType: data?.helper_type,
            preferredLocation: data?.preferred_location,
            sessionFormats: data?.session_formats,
            preferSameGender: data?.prefer_same_gender,
            openToAnyGender: data?.open_to_any_gender,
            requireExpertise: data?.require_expertise,
            dataConsent: data?.data_consent,
            privacySettings: data?.privacy_settings,
            agreeToTerms: data?.agree_to_terms,
            understandLimitations: data?.understand_limitations,
            reflectivePromptsResponses: data?.reflective_prompts_responses,
            voiceDictationTranscript: data?.voice_dictation_transcript,
            contextMediaUrls: data?.context_media_urls,
            completedAt: data?.completed_at
          },
          error: null
        };
      }

      return { data: null, error: null };
    } catch (error) {
      console.error('Get intake error:', error);
      return { data: null, error };
    }
  }
};